import React, { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { NetworkStatus } from '@apollo/client';
import { Card, Grid, Typography, ListItemText } from '@material-ui/core';

import { Dimensions, ErrorComponent, JwlTable, LoadingSpinner } from '../../../../components';
import { userLanguage } from '../../../../core/AuthContext';

import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import AccessoryInventoryStatsWidget from '../AccessoryInventoryStatsWidget';
import { GQLDeliveryUnit, GQLParty } from '@jewelutions/core-crm-server-api-client-domain';
import { ProducerNavigation } from '../../navigation/ProducerNavigation';

interface IProps {
  party: GQLParty;
  deliveryUnits: GQLDeliveryUnit[];
}

const AccessoryDetails: React.FC<IProps> = ({ party, deliveryUnits }: IProps) => {
  const [errorComponentState, setErrorComponentState] = useState(false);

  const accessoryId = ProducerNavigation.accessoryDetails.paramsFromPath(useRouteMatch()).accessoryId;

  const { loading, error, refetch, networkStatus, data } = GQLOperationBuilder.accessory.getAccessory({
    accessoryId: accessoryId
  });

  if (loading || networkStatus === NetworkStatus.refetch) return <LoadingSpinner />;

  if (error && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={error?.stack}
        callback={refetch}
        message={error?.message}
      />
    );

  const getAccessoryModelsRows = () => {
    return GQLOperationBuilder.accessory
      .accessoryAssetFromResponse(data)
      .accessory.models.map(accessoryModel => {
        return {
          id: accessoryModel.id,
          cells: [accessoryModel.sizeType, <Dimensions dimensions3D={accessoryModel.dimensions} />]
        };
      });
  };

  return (
    <Grid container direction="row">
      <Grid item md={6} style={{ marginBottom: '8px' }}>
        <Card style={{ padding: '8px' }}>
          <div style={{ display: 'flex' }}>
            <Typography style={{ marginRight: '2px' }} variant={'h6'}>
              Accessory Details
            </Typography>
          </div>
          <ListItemText
            primary="Accessory name"
            secondary={
              GQLOperationBuilder.accessory.accessoryAssetFromResponse(data).accessory.title[userLanguage]
            }
          />
          <ListItemText
            primary="Accessory description"
            secondary={
              GQLOperationBuilder.accessory.accessoryAssetFromResponse(data).accessory.description[
                userLanguage
              ]
            }
          />
        </Card>
      </Grid>
      {deliveryUnits.map(deliveryUnit => {
        return (
          <AccessoryInventoryStatsWidget
            key={deliveryUnit.id}
            party={party}
            deliveryUnit={deliveryUnit}
            accessoryId={accessoryId}
          />
        );
      })}
      <JwlTable
        title="Accessory Models"
        description={`Accessory models available for ${
          GQLOperationBuilder.accessory.accessoryAssetFromResponse(data).accessory.title[userLanguage]
        }`}
        columns={['Size', 'Dimensions']}
        rows={getAccessoryModelsRows()}
      />
    </Grid>
  );
};

export default AccessoryDetails;
